import { useEffect, useState } from 'react';
import {
  LIBELLES_STATUT,
  type CandidatProbable,
  type DepotCvDetail,
  type ReponseActionDepotCv,
  type StatutCandidature,
} from '@rh/shared';
import { appel } from '../api';
import { nomComplet } from '../format';
import { ChoixCandidatureCv, RechercheFicheCv, type CandidatureProposee } from './ChoixCandidatureCv';
import { Erreur } from './Etat';

export interface CandidatureMemePersonne {
  id: string;
  annonceReference: string | null;
  annonceIntitule: string;
  statut: StatutCandidature;
  recueLe: string;
}

interface Props {
  depotId: string;
  candidatureActuelle: string;
  nomActuel: string;
  /** Autres candidatures de la même personne (depuis la fiche). */
  memePersonne?: readonly CandidatureMemePersonne[];
  onDeplace: (reponse: ReponseActionDepotCv, destination: string) => void;
  onAnnuler: () => void;
}

/**
 * « Déplacer vers une autre candidature » : une autre annonce de la même
 * personne, un candidat probable ou une fiche cherchée. Un CV rattaché ne
 * revient jamais en attente : on le déplace ou on le retire.
 */
export function DeplacerCv({ depotId, candidatureActuelle, nomActuel, memePersonne = [], onDeplace, onAnnuler }: Props) {
  const [probables, setProbables] = useState<CandidatProbable[] | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    appel<DepotCvDetail>(`/cv-a-rattacher/${depotId}`)
      .then((detail) => vivant && setProbables(detail.candidatsProbables))
      .catch(() => vivant && setProbables([]));
    return () => {
      vivant = false;
    };
  }, [depotId]);

  async function deplacer(candidatureId: string, destination: string): Promise<void> {
    if (!window.confirm(`Déplacer ce CV vers ${destination} ? Il quitte la fiche de ${nomActuel}.`)) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const reponse = await appel<ReponseActionDepotCv>(`/cv-a-rattacher/${depotId}/deplacer`, {
        method: 'POST',
        body: JSON.stringify({ candidatureId }),
      });
      onDeplace(reponse, destination);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Déplacement impossible');
    } finally {
      setEnvoi(false);
    }
  }

  const choisir = (candidat: CandidatProbable, candidature: CandidatureProposee) =>
    void deplacer(candidature.candidatureId, `${nomComplet(candidat)} — ${candidature.annonceIntitule}`);
  const autres = memePersonne.filter((c) => c.id !== candidatureActuelle);

  return (
    <div className="panneau-cv">
      <h4>Déplacer vers une autre candidature</h4>
      {autres.length > 0 && (
        <>
          <p className="mention">Autres candidatures de {nomActuel} :</p>
          <ul className="candidatures-cv">
            {autres.map((candidature) => (
              <li key={candidature.id}>
                <div className="corps">
                  <span className="intitule">{candidature.annonceIntitule}</span>
                  {candidature.annonceReference && <code>{candidature.annonceReference}</code>}
                  <span className="meta">
                    <span>reçue le {new Date(candidature.recueLe).toLocaleDateString('fr-FR')}</span>
                    <span>{LIBELLES_STATUT[candidature.statut] ?? candidature.statut}</span>
                  </span>
                </div>
                <button
                  type="button"
                  className="bouton"
                  disabled={envoi}
                  aria-label={`Déplacer ici : ${nomActuel} — ${candidature.annonceIntitule}`}
                  onClick={() => void deplacer(candidature.id, `${nomActuel} — ${candidature.annonceIntitule}`)}
                >
                  Déplacer ici
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="mention">Autres fiches proches de ce CV :</p>
      {probables === null ? (
        <p className="mention">Recherche des fiches proches…</p>
      ) : probables.length === 0 ? (
        <p className="mention">Aucune autre fiche proche : cherchez-la ci-dessous.</p>
      ) : (
        <ChoixCandidatureCv
          candidats={probables}
          libelleAction="Déplacer ici"
          exclure={candidatureActuelle}
          envoi={envoi}
          onChoisir={choisir}
        />
      )}
      <RechercheFicheCv libelleAction="Déplacer ici" exclure={candidatureActuelle} envoi={envoi} onChoisir={choisir} />
      {erreur && <Erreur message={erreur} />}
      <div className="actions">
        <button type="button" className="lien" onClick={onAnnuler}>
          Annuler le déplacement
        </button>
      </div>
    </div>
  );
}
